
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import * as log from 'loglevel';
  import {
    Client,
    IInvoiceBatchModel,
    IInvoiceModel,
    ILineItemModel,
    UpdateInvoiceBatchRequestModel
  } from '@/services/api.service';
  import EditInvoiceBatch from '@/views/accounting/batches/edit/EditInvoiceBatch.vue';
  import { InvoiceBatchUpdatedMessage } from '@/services/eventbus.models';
  import { TableField } from '@/components/commonui/TableField';
  import { EventBus } from '@/event-bus';
  import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
  import toCurrencyFormatter from '@/filters/tocurrency.formatter.ts';

  export class InvoiceDetail {
    public gid: string | undefined = '';
    public jobGid: string | undefined = '';
    public clientGid: string | undefined = '';
    public lineItemCount: number = 0;
    public amountExTax: number = 0;
    public amountTax: number = 0;
    public amountIncTax: number = 0;
    public invoiceNumber: string = '';
    public clientName: string = '';
    public jobName: string = '';
    public jobNumber: string = '';
    public lineItems: LineItemDetail[] = [];
  }
  export class LineItemDetail {
    public gid: string = '';
    public comment: string = '';
    public accountCode: string = '';
    public description: string = '';
    public amountExTax: number = 0;
    public amountTax: number = 0;
    public amountIncTax: number = 0;
  }

  @Component({
    components: {
      EditInvoiceBatch
    },
    filters: {
      toCurrency: toCurrencyFormatter
    }
  })
  export default class ViewInvoiceBatch extends Vue {
    private gid: string = '';
    private batch: IInvoiceBatchModel | null = null;
    private invoices: InvoiceDetail[] = [];
    private batchName: string = '';
    private exportBatchOnClose: boolean = false;

    private batchLoaded: boolean = false;

    private get canEdit(): boolean {
      if (this.batch === null) { return false; }
      return this.batch.closedDateTimeUtc === undefined;
    }
    private get canClose(): boolean {
      if (this.batch === null) { return false; }
      return this.batch.closedDateTimeUtc === undefined;
    }
    private get canExport(): boolean {
      if (this.batch === null) { return false; }
      return !(this.batch.closedDateTimeUtc === undefined);
    }

    private isClosingBatch: boolean = false;

    private tableFields: TableField[] = [
      new TableField({ key: 'invoiceNumber', label: 'Invoice #', sortable: true }),
      new TableField({ key: 'clientName', label: 'Client', sortable: true }),
      new TableField({ key: 'jobNumber', label: 'Job #', sortable: true }),
      new TableField({ key: 'jobName', label: 'Job Name', sortable: true }),
      new TableField({ key: 'lineItemCount', label: 'Line Items', sortable: true }),
      new TableField({ key: 'amountExTax', label: 'Ex GST', formatter: this.formatAsCurrency }),
      new TableField({ key: 'amountTax', label: 'GST', formatter: this.formatAsCurrency }),
      new TableField({ key: 'amountIncTax', label: 'Inc GST', formatter: this.formatAsCurrency }),
      new TableField({ key: 'show_details', label: '' })
    ];

    protected mounted() {
      // get the networks
      this.gid = this.$route.params.gid;

      // @ts-ignore
      this.fetchBatchData(this.gid);
    }

    private async fetchBatchData(batchGid: string) {
      // @ts-ignore
      this.$Progress.start();
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      const [theBatch, theInvoices] = await Promise.all([
        clientService.getInvoiceBatch(batchGid),
        clientService.getBatchInvoices(batchGid)
      ]);

      this.batch = theBatch;
      this.batchName = theBatch.batchName || '';

      this.invoices = theInvoices.map(item => this.invoiceModelToDetail(item));
      this.batchLoaded = true;

      // @ts-ignore
      this.$Progress.finish();
    }

    private invoiceModelToDetail(item: IInvoiceModel): InvoiceDetail {
      const inv = new InvoiceDetail();
      inv.gid = item.gid;
      inv.jobGid = item.jobGid;
      inv.clientGid = item.clientGid;

      inv.amountExTax = item.amountExTax || 0;
      inv.amountTax = item.amountTax || 0;
      inv.amountIncTax = item.amountIncTax || 0;
      inv.invoiceNumber = item.invoiceNumber || '';
      if (item.client) {
        inv.clientName = item.client.name || '';
      }
      if (item.job) {
        inv.jobNumber = item.job.jobNumber || '';
        inv.jobName = item.job.name || '';
      }
      // Build the line items
      inv.lineItemCount = 0;
      if (item.lineItems) {
        inv.lineItemCount = item.lineItems.length;
        inv.lineItems = item.lineItems.map(lineItem => this.lineItemModelToDetail(lineItem));
      }

      console.log({ inv });
      return inv;
    }

    private lineItemModelToDetail(item: ILineItemModel): LineItemDetail {
      const li = new LineItemDetail();
      li.gid = item.gid || '';
      li.description = item.description || '';
      li.comment = item.comment || '';
      li.accountCode = item.accountCode || '';
      li.amountExTax = item.amountExTax || 0;
      li.amountTax = item.amountTax || 0;
      li.amountIncTax = item.amountIncTax || 0;

      return li;
    }

    private formatAsCurrency(value: number) {
      if (typeof value !== 'number') {
        return value;
      }
      const formatter: Intl.NumberFormat = new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2
      });
      return formatter.format(value);
    }

    private onEditInvoiceBatchRequested() {
      this.$modal.show('modal-invoicebatch-edit');
    }

    private onCloseInvoiceBatchRequested() {
      // @ts-ignore
      this.$refs.closeinvoicebatchprompt.show();
    }

    private onContinueCloseInvoiceBatch() {
      console.log('EVENT:Confirmed close invoice batch');
      const batchGid = this.gid;
      if (this.batch) {
        this.isClosingBatch = true;
        const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
        const request = new UpdateInvoiceBatchRequestModel();
        request.init({
          batchName: this.batch.batchName,
          gid: batchGid,
          openDateTimeUtc: this.batch.openDateTimeUtc,
          closedDateTimeUtc: new Date(),
          isDeleted: this.batch.isDeleted,
          defaultInvoiceDate: this.batch.defaultInvoiceDate,
          useActivityDateAsInvoiceDate: this.batch.useActivityDateAsInvoiceDate,
          clientGid: this.batch.clientGid
        });

        clientService
          .updateInvoiceBatch(batchGid, request)
          .then(batch => {
            // Schedule the item if the schedule is set
            // otherwise complete out the process
            // @ts-ignore
            this.$Progress.finish();
            // @ts-ignore
            this.$awn.success('Invoice batch ' + this.batchName + ' updated successfully');
            // Raise the created events, and publish the event on the bus
            this.$emit('invoice-batch-updated', batch);
            EventBus.$emit('invoice-batch-updated', new InvoiceBatchUpdatedMessage(batch));
            // now download the file
            // this.downloadAccountFile(batchGid);
            if (this.batch) { this.batch.closedDateTimeUtc = batch.closedDateTimeUtc; }

            // If export is requested run download export file
            if (this.exportBatchOnClose) {
              this.onExportInvoiceBatchRequested();
            }

            this.isClosingBatch = false;
          })
          .catch(reason => {
            // @ts-ignore
            this.$Progress.fail();
            // @ts-ignore
            this.$awn.alert('Failed to update the invoice batch.\r\n' + reason.message);
            this.isClosingBatch = false;
          });
      }
    }

    private onExportInvoiceBatchRequested() {
      const baseUrl = process.env.VUE_APP_API_URL;
      const downloadUrl = baseUrl + '/api/v1/accounting/invoicebatches/' + this.gid + '/export';
      axios({
        method: 'get',
        url: downloadUrl,
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      })
        .then((response: any) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `MYOB-${this.batchName}.zip`);
          document.body.appendChild(link);
          link.click();
        })
        .catch(reason => {
          // @ts-ignore
          this.$awn.alert('Unable to download the file\r\n' + reason.message);
        });
    }
    private onInvoiceBatchUpdated(message: InvoiceBatchUpdatedMessage) {
      // Locate the item in the list and updated the values
      if (message && message.data && message.data) {
        const theGid = message.data.gid;
        Object.assign(this.batch, message.data);
      }
    }
  }
